import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  CLIENT_URL_PARAM,
  ClientDetailSection,
  readClientDetailSection,
  SECTION_URL_PARAM,
  stripClientDetailParams,
} from '../utils/client-detail-url.utils';

/**
 * Keeps the selected client drawer + section in sync with the URL
 * (`client`, `section`) for deep links, refresh, and browser history.
 */
export function useManageClientsUrlParams() {
  const [searchParams, setSearchParams] = useSearchParams();

  const clientId = searchParams.get(CLIENT_URL_PARAM);
  const section = readClientDetailSection(searchParams);

  const openClient = useCallback(
    (extClientId: string) => {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.set(CLIENT_URL_PARAM, extClientId);
      nextParams.delete(SECTION_URL_PARAM);
      setSearchParams(nextParams);
    },
    [searchParams, setSearchParams]
  );

  const closeClient = useCallback(() => {
    setSearchParams(stripClientDetailParams(searchParams));
  }, [searchParams, setSearchParams]);

  const setSection = useCallback(
    (next: ClientDetailSection) => {
      const nextParams = new URLSearchParams(searchParams);
      if (next === 'access') {
        nextParams.delete(SECTION_URL_PARAM);
      } else {
        nextParams.set(SECTION_URL_PARAM, next);
      }
      setSearchParams(nextParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const clearClientParams = useCallback(() => {
    setSearchParams(stripClientDetailParams(searchParams), { replace: true });
  }, [searchParams, setSearchParams]);

  return {
    clientId,
    section,
    openClient,
    closeClient,
    setSection,
    clearClientParams,
    searchParams,
  };
}
